/**
 * Utilidades para validación de IBAN
 * Soporte inicial para España (ES), preparado para expansión europea
 */

export interface IbanValidation {
  isValid: boolean;
  error?: string;
  formatted?: string; // IBAN con espacios para mostrar (ES66 2100 0418...)
  clean?: string;     // IBAN sin espacios para BD (ES6621000418...)
}

// Configuración por país - SEPA principales
const IBAN_CONFIG = {
  ES: { 
    length: 24, 
    name: 'España',
    format: 'ES + 2 dígitos + 20 caracteres alfanuméricos'
  },
  DE: { 
    length: 22, 
    name: 'Alemania',
    format: 'DE + 2 dígitos + 18 caracteres alfanuméricos'
  },
  BE: { 
    length: 16, 
    name: 'Bélgica',
    format: 'BE + 2 dígitos + 12 caracteres alfanuméricos'
  },
  FR: { 
    length: 27, 
    name: 'Francia',
    format: 'FR + 2 dígitos + 23 caracteres alfanuméricos'
  }
};

/**
 * Limpia el IBAN eliminando espacios, guiones y caracteres especiales
 */
export const cleanIban = (iban: string): string => {
  return iban.replace(/[\s\-\.]/g, '').toUpperCase();
};

/**
 * Formatea el IBAN con espacios cada 4 caracteres para mejor legibilidad
 */
export const formatIban = (iban: string): string => {
  const clean = cleanIban(iban);
  return clean.replace(/(.{4})/g, '$1 ').trim();
};

/**
 * Valida el checksum del IBAN usando el algoritmo mod-97 estándar ISO 13616
 */
const validateIbanChecksum = (iban: string): boolean => {
  const clean = cleanIban(iban);
  
  // Mover los primeros 4 caracteres al final
  const rearranged = clean.slice(4) + clean.slice(0, 4);
  
  // Convertir letras a números (A=10, B=11, ..., Z=35)
  const numberString = rearranged.replace(/[A-Z]/g, (letter) => {
    return (letter.charCodeAt(0) - 55).toString();
  });
  
  // Calcular mod 97
  let remainder = 0;
  for (let i = 0; i < numberString.length; i++) {
    remainder = (remainder * 10 + parseInt(numberString[i])) % 97;
  }
  
  return remainder === 1;
};

/**
 * Valida el formato básico del IBAN (longitud y estructura)
 */
const validateIbanFormat = (iban: string): { isValid: boolean; error?: string } => {
  const clean = cleanIban(iban);
  
  // Verificar que no esté vacío
  if (!clean) {
    return { isValid: true }; // Campo vacío es válido (opcional)
  }
  
  // Verificar que solo contenga letras y números
  if (!/^[A-Z0-9]+$/.test(clean)) {
    return { 
      isValid: false, 
      error: 'El IBAN solo puede contener letras y números' 
    };
  }
  
  // Verificar código de país
  const countryCode = clean.slice(0, 2);
  if (!IBAN_CONFIG[countryCode as keyof typeof IBAN_CONFIG]) {
    return { 
      isValid: false, 
      error: `Código de país ${countryCode} no soportado. Países válidos: ES, DE, BE, FR` 
    };
  }
  
  // Verificar longitud
  const config = IBAN_CONFIG[countryCode as keyof typeof IBAN_CONFIG];
  const expectedLength = config.length;
  if (clean.length !== expectedLength) {
    return { 
      isValid: false, 
      error: `El IBAN de ${config.name} debe tener exactamente ${expectedLength} caracteres` 
    };
  }
  
  // Verificar que los dígitos de control sean números
  const checkDigits = clean.slice(2, 4);
  if (!/^\d{2}$/.test(checkDigits)) {
    return { 
      isValid: false, 
      error: 'Los dígitos de control deben ser números' 
    };
  }
  
  return { isValid: true };
};

/**
 * Función principal de validación de IBAN
 * @param iban - IBAN a validar (con o sin espacios)
 * @param countries - Array de países soportados (opcional, por defecto solo ES)
 * @returns Objeto con resultado de validación y datos formateados
 */
export const validateIban = (iban: string, countries: string[] = ['ES']): IbanValidation => {
  const clean = cleanIban(iban);
  
  // Si está vacío, es válido (campo opcional)
  if (!clean) {
    return {
      isValid: true,
      formatted: '',
      clean: ''
    };
  }
  
  // Validar formato básico
  const formatValidation = validateIbanFormat(clean);
  
  if (!formatValidation.isValid) {
    return {
      isValid: false,
      error: formatValidation.error,
      formatted: formatIban(iban),
      clean
    };
  }
  
  // Validar checksum
  const checksumValid = validateIbanChecksum(clean);
  
  if (!checksumValid) {
    return {
      isValid: false,
      error: 'Hay un error en el formato del IBAN',
      formatted: formatIban(iban),
      clean
    };
  }
  
  // Todo válido
  return {
    isValid: true,
    formatted: formatIban(clean),
    clean
  };
};

/**
 * Función auxiliar para obtener el país de un IBAN
 */
export const getCountryFromIban = (iban: string): string | null => {
  const clean = cleanIban(iban);
  if (clean.length < 2) return null;
  return clean.slice(0, 2);
};

/**
 * Función auxiliar para verificar si un país es soportado
 */
export const isCountrySupported = (countryCode: string): boolean => {
  return countryCode in IBAN_CONFIG;
};